import { DrawerBody, DrawerFooter, SubmitButton, TextField, isEmail } from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { Formik, useField, useFormikContext } from "formik";
import { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { TenantOptionResource } from "../../../../shared/types/tenant";
import { TagMultiSelectField } from "../../../../shared/ui/TagSelectField";
import { UserTenantMultiSelect } from "../../../../shared/ui/UserTenantSelect";
import { EmailTypeSelectField } from "../../contacts/selects/EmailTypeSelectField";
import { PhoneTypeSelectField } from "../../contacts/selects/PhoneTypeSelectField";
import { contactService } from "../../shared/api/contactService";
import { ContactTypeSingleSelect } from "../../shared/select/ContactTypeSingleSelect";
import { LanguageSingleSelectField } from "../../shared/select/LanguageSingleSelectField";
import { ContactEmailType, ContactPhoneType, TagIndexResource } from "../../shared/types";
import { ContactTypeOptionResource } from "../../shared/types/contactType";
import { ContactExistsNotice } from "./ContactExistsNotice";
import { CreateContactStepForm, DrawerForm, FormActions } from "./Form";

export interface MasterDataFormValues {
	contactType: ContactTypeOptionResource | null;
	firstName: string;
	lastName: string;
	primaryEmail: string;
	primaryEmailType: ContactEmailType;
	primaryPhoneNumber: string;
	primaryPhoneNumberType: ContactPhoneType;
	tags: TagIndexResource[];
	tenants: TenantOptionResource[];
	locale: string;
}

export const emptyMasterDataFormValues: MasterDataFormValues = {
	contactType: null,
	firstName: "",
	lastName: "",
	primaryEmail: "",
	primaryEmailType: "work",
	primaryPhoneNumber: "",
	primaryPhoneNumberType: "workLandline",
	tags: [],
	tenants: [],
	locale: "",
};

const TwoColumns = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: ${({ theme }) => theme.spacing(2)};
`;

interface Props extends CreateContactStepForm<MasterDataFormValues> {
	setIsDirty: (dirty: boolean) => void;
}

interface MasterDataFormFieldsProps {
	setIsDirty: (dirty: boolean) => void;
}

const MasterDataFormFields: FC<MasterDataFormFieldsProps> = ({ setIsDirty }) => {
	const { t } = useTranslation();
	const { dirty } = useFormikContext();
	const [primaryEmailField] = useField<string>("primaryEmail");

	useEffect(() => {
		setIsDirty(dirty);
	}, [dirty, setIsDirty]);

	return (
		<>
			<ContactTypeSingleSelect name="contactType" label={t("Contact type")} required />
			<TextField name="firstName" label={t("Firstname")} required />
			<TextField name="lastName" label={t("Lastname")} required />
			<TwoColumns>
				<TextField name="primaryEmail" label={t("Primary email address")} required />
				<EmailTypeSelectField name="primaryEmailType" label={t("Type")} />
			</TwoColumns>
			{primaryEmailField.value && <ContactExistsNotice email={primaryEmailField.value} />}
			<TwoColumns>
				<TextField name="primaryPhoneNumber" label={t("Primary phone number")} />
				<PhoneTypeSelectField name="primaryPhoneNumberType" label={t("Type")} />
			</TwoColumns>
			<TagMultiSelectField name="tags" label={t("Tags")} />
			<UserTenantMultiSelect name="tenants" label={t("Tenants")} required />
			<LanguageSingleSelectField name="locale" label={t("Language")} required />
		</>
	);
};

export const MasterDataForm: FC<Props> = ({ setIsDirty, initialValues, onSubmit }) => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();

	const validate = async (values: MasterDataFormValues) => {
		const errors: { [K in keyof MasterDataFormValues]?: string } = {};

		if (!values.contactType) {
			errors.contactType = t("Required field");
		}
		if (!values.firstName) {
			errors.firstName = t("Required field");
		}
		if (!values.lastName) {
			errors.lastName = t("Required field");
		}
		if (!values.primaryEmail) {
			errors.primaryEmail = t("Required field");
		} else if (!isEmail(values.primaryEmail)) {
			errors.primaryEmail = t("Invalid email address");
		} else {
			const checkEmail = await queryClient.fetchQuery({
				queryKey: contactService.keys.checkEmail(values.primaryEmail),
				queryFn: () => contactService.checkEmail(values.primaryEmail),
			});
			if (checkEmail) {
				errors.primaryEmail = t("Email address cannot be used to create a new contact.");
			}
		}
		if (values.tenants.length == 0) {
			errors.tenants = t("Required field");
		}
		if (!values.locale) {
			errors.locale = t("Required field");
		}

		return errors;
	};

	return (
		<Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate} validateOnChange={false}>
			<DrawerForm>
				<DrawerBody>
					<MasterDataFormFields setIsDirty={setIsDirty} />
				</DrawerBody>
				<DrawerFooter>
					<span />
					<FormActions>
						<SubmitButton>{t("Next")}</SubmitButton>
					</FormActions>
				</DrawerFooter>
			</DrawerForm>
		</Formik>
	);
};
